import { useEffect } from "react";

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, prev, created };
}

// Dependency-free SEO head manager: title, description, canonical, robots,
// Open Graph / Twitter tags and a JSON-LD structured-data block. Restores the
// previous head state on unmount so single-page navigation stays clean.
export function usePageSeo({ title, description, canonicalPath, image, jsonLd } = {}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = canonicalPath ? origin + canonicalPath : (typeof window !== "undefined" ? window.location.href : "");

    let canon = document.head.querySelector('link[rel="canonical"]');
    const prevCanon = canon ? canon.getAttribute("href") : null;
    const canonCreated = !canon;
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    if (url) canon.setAttribute("href", url);

    const metas = [];
    const track = (m) => { if (m) metas.push(m); };
    track(upsertMeta("name", "description", description));
    track(upsertMeta("name", "robots", "index,follow,max-image-preview:large"));
    track(upsertMeta("property", "og:title", title));
    track(upsertMeta("property", "og:description", description));
    track(upsertMeta("property", "og:type", "website"));
    if (url) track(upsertMeta("property", "og:url", url));
    if (image) track(upsertMeta("property", "og:image", image));
    track(upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary"));
    track(upsertMeta("name", "twitter:title", title));
    track(upsertMeta("name", "twitter:description", description));
    if (image) track(upsertMeta("name", "twitter:image", image));

    let script = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "page");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      if (canonCreated) canon.remove();
      else if (prevCanon != null) canon.setAttribute("href", prevCanon);
      metas.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute("content", prev);
      });
      if (script) script.remove();
    };
  }, [title, description, canonicalPath, image, jsonLdKey]);
}
